import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Buildings, GoogleLogo, Envelope, Lock } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Phase 1024 — Login returns the role; clerk users go straight to the
      // dedicated shell at /clerk. Admins/staff keep the normal flow.
      const result = await login(email, password);
      toast.success(t('welcome') + '!');
      if (result?.role === 'building_payments_clerk') {
        navigate('/clerk');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1559329146-807aff9ff1fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc3NDk1MTI3M3ww&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-zinc-950/60" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <Buildings size={40} weight="fill" />
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              CleanPro Ops
            </span>
          </div>
          <div>
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              {language === 'el' ? 'Διαχείριση Καθαρισμού Κτιρίων' : 'Building Cleaning Management'}
            </h1>
            <p className="text-zinc-300 text-lg">
              {language === 'el' 
                ? 'Απλή, γρήγορη και αποτελεσματική διαχείριση για εταιρείες καθαρισμού.'
                : 'Simple, fast, and efficient management for cleaning companies.'}
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex flex-col justify-center px-6 lg:px-16 bg-white">
        {/* Language Toggle */}
        <div className="absolute top-6 right-6">
          <button
            onClick={toggleLanguage}
            className="px-3 py-1 border border-zinc-200 text-sm font-medium hover:bg-zinc-50 transition-colors"
            data-testid="language-toggle"
          >
            {language === 'en' ? 'EL' : 'EN'}
          </button>
        </div>

        <div className="mx-auto w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <Buildings size={32} weight="fill" className="text-zinc-900" />
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              CleanPro Ops
            </span>
          </div>

          <h2 className="text-2xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('login')}
          </h2>
          <p className="text-zinc-500 mb-8">
            {language === 'el' ? 'Εισάγετε τα στοιχεία σας για πρόσβαση' : 'Enter your credentials to access your account'}
          </p>

          {/* Google Login */}
          <Button
            onClick={loginWithGoogle}
            variant="outline"
            className="w-full mb-6 py-6 border-zinc-200 hover:bg-zinc-50"
            data-testid="google-login-btn"
          >
            <GoogleLogo size={20} weight="bold" className="mr-2" />
            {t('loginWithGoogle')}
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-zinc-500">{t('orContinueWith')}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-zinc-700">{t('email')}</Label>
              <div className="relative mt-1">
                <Envelope size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="email"
                  type="email"
                  name="username"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 py-5 border-zinc-200 rounded-none"
                  placeholder="email@example.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-zinc-700">{t('password')}</Label>
              <div className="relative mt-1">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="password"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 py-5 border-zinc-200 rounded-none"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>
              {/* Phase 1020 — Forgot password link */}
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-zinc-900 underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full py-6 bg-zinc-950 text-white rounded-none hover:bg-zinc-800"
              disabled={isLoading}
              data-testid="login-submit-btn"
            >
              {isLoading ? t('loading') : t('signIn')}
            </Button>
          </form>

          {/* Phase 1019 — "Sign Up" link REMOVED. Self-registration is
              disabled; only administrators can create user accounts. */}
        </div>
      </div>
    </div>
  );
}
